import React, { Fragment, useEffect, useState } from 'react';
import { Trans } from '@common/i18n/trans';
import { useFormContext } from 'react-hook-form';
import { Button } from '@common/ui/buttons/button';
import { useTrans } from '@common/i18n/use-trans';
import { DialogTrigger } from '@common/ui/overlays/dialog/dialog-trigger';
import { UploadDialog } from './rate-sheet-upload';
import { FileItem } from './file-item';
import moment from 'moment-timezone';
import { RowSheetName } from './row-sheet-name';
import { useDownloadMdRateSheet } from '@app_modules/requests/use-download-md-rate-sheet';
import { slugifyString } from '@common/utils/string/slugify-string';
import { downloadFile } from '@app_modules/document-requests/reports/requests/use-download-bsa-report';
import { DownloadIcon } from '@common/icons/material/Download';
import { useAuth } from '@common/auth/use-auth';

interface RowSheetProps {
  id: number;
  market_rate_date: string;
  investor_code: string;
  item: any;
  subItems: any[];
  pidx: number;
  uploadedFileItems: any;
  onAddSubItems: (pidx: number, subItems: any[]) => void;
  setCnt: (cnt: number) => void;
  cnt: number;
  loan_program_types: string[];
  changeFileItems: (fileItems: FileItem[]) => void;
  onSelectedSheetNamesChange?: (pidx: number, selectedSheetNames: string[]) => void;
  onSelectedLoanProgramTypesChange?: (pidx: number, selectedLoanProgramTypes: { sheetName: string; types: string[] }[]) => void;
}

export function RowSheet({
  id,
  market_rate_date,
  investor_code,
  item,
  subItems,
  pidx,
  uploadedFileItems,
  onAddSubItems,
  setCnt,
  cnt,
  loan_program_types,
  changeFileItems,
  onSelectedSheetNamesChange,
  onSelectedLoanProgramTypesChange,
}: RowSheetProps) {
  const {user} = useAuth();
  const user_id = user?.id;

  const { trans } = useTrans();
  const { watch } = useFormContext<any>();
  const loan_program_type = watch(`${pidx}_loan_program_type`);
  const sheet_name = watch(`${pidx}_sheet_name`);

  const [itemStatus, setItemStatus] = useState<{
    status: string;
    total_files: number;
    uploaded_date: string;
    file_name: string;
    error?: string;
  }>(
    item.fileItems && item.fileItems[loan_program_type + '-' + investor_code]
      ? item.fileItems[investor_code + '-' + loan_program_type]
      : { status: '', total_files: 0, uploaded_date: '', file_name: '' }
  );

  const [fileItems, setFileItems] = useState<FileItem[]>([]);
  const [sheetNames, setSheetNames] = useState<any[]>([]);
  const [selectedSheetNames, setSelectedSheetNames] = useState<string[]>([]); // Default to empty
  const [selectedLoanProgramTypes, setSelectedLoanProgramTypes] = useState<{ sheetName: string; types: string[] }[]>([]);

  const addFiles = (pidx: number, files: File[]) => {
    const newFileItem: FileItem = {
      key: files[0].name,
      files,
      investor_code,
      loan_program_type,
      sheet_name,
      status: 'Uploaded',
      total_files: files.length,
      file_name: files[0].name,
    };
    setFileItems([newFileItem]);
    changeFileItems([newFileItem]);
    setItemStatus({
      status: 'Uploaded',
      total_files: files.length,
      file_name: files[0].name,
      uploaded_date: moment().format('MM/DD/YYYY'),
    });
  };

  const onProcess = (status: string, total_files: number, uploaded_date: string, file_name: string) => {
    if (total_files) {
      setItemStatus({ status, total_files, uploaded_date, file_name });
      setFileItems([]);
    }
  };

  useEffect(() => {
    if (item.sheet_names) {
      const sheets = Object.values(item.sheet_names);
      setSheetNames(sheets);
      // Only include sheets where check is true in selectedSheetNames
      const initialSelected = sheets
        .filter((sheet: any) => sheet.check === true)
        .map((sheet: any) => sheet.name);
      setSelectedSheetNames(initialSelected);
      // Initialize selectedLoanProgramTypes
      const initialSelections = sheets.map((sheet: any) => ({
        sheetName: sheet.name,
        types: sheet.loan_program_types || [],
      }));
      setSelectedLoanProgramTypes(initialSelections);
    }
  }, [item]);

  useEffect(() => {
    if (item) {
      setItemStatus({
        status: item.status || '',
        error: item.error,
        total_files: item.total_files || 0,
        uploaded_date: item.uploaded_date || '',
        file_name: item.file_name || '',
      });
    }
  }, [item]);

  useEffect(() => {
    if (onSelectedSheetNamesChange) {
      onSelectedSheetNamesChange(pidx, selectedSheetNames);
    }
  }, [selectedSheetNames, pidx, onSelectedSheetNamesChange]);

  useEffect(() => {
    if (onSelectedLoanProgramTypesChange) {
      onSelectedLoanProgramTypesChange(pidx, selectedLoanProgramTypes);
    }
  }, [selectedLoanProgramTypes, pidx, onSelectedLoanProgramTypesChange]);

  const downloadMd = useDownloadMdRateSheet();

  return (
    <tr key={`meta-${pidx}-${id}`} className="border-b transition duration-300 ease-in-out hover:bg-gray-100 w-[100%]">
      <td
        className="text-wrap pl-40 w-[10%] text-sm text-gray-900 font-light px-6 py-4 whitespace-nowrap border-r"
        colSpan={2}
        rowSpan={sheetNames.length || 1}
      >
        <Fragment>
          <DialogTrigger type="modal">
            <Button size="xs" variant="outline" color="orange" className="mr-10" disabled={!investor_code}>
              <Trans message="Upload" />
            </Button>
            <UploadDialog pidx={pidx} addFiles={addFiles} />
          </DialogTrigger>
          {itemStatus && itemStatus.file_name ? <>{itemStatus.file_name}</> : null}
        </Fragment>
      </td>
      <td className="w-[10%] text-sm text-gray-900 px-6 py-4 whitespace-nowrap" colSpan={2}>
        
          {sheetNames.length > 0 ? (<>
            <table className='w-full'>
            {sheetNames?.map((sheetItem: any, index: number) => (
            <tr className='w-full border-b'>
              <td className='w-[40%] text-sm text-gray-900 px-6 py-4 whitespace-nowrap'>
                <RowSheetName
                  key={`${pidx}-${index}-${sheetItem}`}
                  pidx={pidx}
                  index={index}
                  sheet_item={sheetItem}
                  investor_code={investor_code}
                  loan_program_types={loan_program_types}
                  selectedSheetNames={selectedSheetNames}
                  setSelectedSheetNames={setSelectedSheetNames}
                  selectedLoanProgramTypes={selectedLoanProgramTypes}
                  setSelectedLoanProgramTypes={setSelectedLoanProgramTypes}
                />
              </td>
              <td className='w-[40%] text-sm text-gray-900 px-6 py-4 whitespace-nowrap'>
                {sheetItem['status']}
              </td>
              <td className='w-[20%] text-sm text-gray-900 px-6 py-4 whitespace-nowrap'>
              {sheetItem['status'] == 'Completed' && (user_id == 1 || user_id == 35) ? (
                <Button
                  variant="outline"
                  color="primary"
                  size={'2xs'}
                  onClick={() => {
                    downloadMd.mutate({id: id, investor_code: investor_code, sheet_name: sheetItem['name']}, {
                      onSuccess: (response: any) => {
                        const file_name = slugifyString(investor_code+"_"+sheetItem['name'], "_").replaceAll("-", "_");
                        downloadFile(file_name+'.md', response)
                      },
                    });
                  }}
                >
                  Raw File <DownloadIcon/>
                </Button>) : (<></>)}
              </td>
            </tr>
          ))}
          </table>
          </>):(<>
          {item.status}
          </>)}
          
      </td>
    </tr>
  );
}